import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { PlusOutlined, StarFilled } from '@ant-design/icons';
import style from './index.scss';
import EventIcon from './EventIcon';
import { EVENT_CATEGORY, isSystemType } from './constants';

/**
 * AC-1: the system-default type is PINNED TO THE TOP of its category list.
 *
 * A copy, not an in-place sort — `types` is the page's state array and sorting it
 * here would mutate what the parent holds.
 *
 * `sort` is stable in every engine we support (ES2019 requires it), so the
 * client's own types keep the order the BE sent them in; only the system type
 * moves. Ordering here rather than in the adapter keeps it a presentation
 * decision, which is what it is.
 */
const pinSystemFirst = (types) =>
  [...types].sort((a, b) => isSystemType(b) - isSystemType(a));

/**
 * The left column — every configured event type for the selected category.
 *
 *   [icon] Login          ★ Default  New
 *          3 on timeline · 5 on click
 *
 * The counts are the design's own wording and come straight from the BE
 * (`spatialFieldCount` / `onClickFieldCount`), so the list never has to load each
 * type's fields just to summarise them.
 *
 * PRESENTATIONAL — selection lives in the page, because switching types has to
 * check for unsaved changes first, which is not this component's business.
 */
const EventTypeList = ({ types, selectedId, category, onSelect, onAddNew }) => (
  <div className={style.typeList}>
    {pinSystemFirst(types).map((type) => {
      const selected = type.id === selectedId;
      return (
        <button
          type="button"
          key={type.id}
          className={`${style.typeItem} ${
            selected ? style.typeItemActive : ''
          }`}
          aria-current={selected ? 'true' : undefined}
          onClick={() => onSelect(type.id)}
        >
          <span className={style.typeItemTop}>
            <EventIcon
              iconKey={type.iconKey}
              technicalValue={type.technicalValue}
              className={style.typeItemIcon}
            />
            <span className={style.typeItemName}>{type.displayName}</span>

            {/* The fallback used for any event type with no configuration of its
                own. One per category — the page enforces that, not this list. */}
            {type.isDefault ? (
              <span className={style.defaultBadge}>
                <StarFilled /> Default
              </span>
            ) : null}

            {/* AC-1: "Each system-default type has a 'System default' badge."
                Rendered ALONGSIDE ★ Default rather than instead of it — a fresh
                install seeds the system type with is_default = TRUE, so both are
                true at once, and they say different things: ★ Default is the
                fallback currently in use, this one is "ships with the product,
                cannot be removed". */}
            {isSystemType(type) ? (
              <span className={style.systemBadge}>System default</span>
            ) : null}

            {type.isNew ? <span className={style.newBadge}>New</span> : null}
          </span>

          <span className={style.typeItemCounts}>
            {type.spatialFieldCount} on timeline · {type.onClickFieldCount} on
            click
          </span>
        </button>
      );
    })}

    <Button
      className={style.addTypeButton}
      icon={<PlusOutlined />}
      onClick={onAddNew}
    >
      {category === EVENT_CATEGORY.ACTIVITY
        ? 'Add new activity type'
        : 'Add new transaction type'}
    </Button>
  </div>
);

EventTypeList.propTypes = {
  types: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      displayName: PropTypes.string.isRequired,
      iconKey: PropTypes.string,
      isDefault: PropTypes.bool,
      isSystem: PropTypes.bool,
      technicalValue: PropTypes.string,
      isNew: PropTypes.bool,
      spatialFieldCount: PropTypes.number,
      onClickFieldCount: PropTypes.number
    })
  ),
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  category: PropTypes.oneOf(Object.values(EVENT_CATEGORY)).isRequired,
  onSelect: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired
};

EventTypeList.defaultProps = { types: [], selectedId: undefined };

export default EventTypeList;
